import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import ApiURl from "../Paths/Api";

// SVG Icons as React components
const SearchIcon = () => (
  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const MinusIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const MeetingReport = () => {
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openMeetingId, setOpenMeetingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch meetings and attendees
  const fetchMeetings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const phone = localStorage.getItem('phone');
      if (!phone) {
        throw new Error("Phone number not found in local storage");
      }

      const response = await fetch(`${ApiURl}/get_meetings.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });

      const result = await response.json();
      if (result.success) {
        setMeetings(result.data);
      } else {
        throw new Error(result.message || "Failed to fetch meetings");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching meetings:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Handle search
  const filteredMeetings = meetings.filter((meeting) =>
    meeting.purpose.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle dropdown
  const toggleDropdown = (meetingId) => {
    setOpenMeetingId(openMeetingId === meetingId ? null : meetingId);
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Purpose", "Location", "Time", "Description", "Status", "Attendees"];
    const csvRows = [headers.join(",")];

    filteredMeetings.forEach((meeting) => {
      const attendeesList = meeting.attendees.map(a => `${a.name} (${a.email})`).join("; ");
      const row = [
        `"${meeting.purpose.replace(/"/g, '""')}"`,
        `"${meeting.location.replace(/"/g, '""')}"`,
        `"${new Date(meeting.time).toLocaleString()}"`,
        `"${meeting.description.replace(/"/g, '""')}"`,
        meeting.status,
        `"${attendeesList.replace(/"/g, '""')}"`,
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", `meetings_report_${new Date().toISOString().split('T')[0]}.csv`);
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
   <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans" >
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-4 shadow-lg fixed w-full z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="text-xl font-bold tracking-tight">Meeting Report</div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-base font-medium hover:bg-teal-700 transition-all duration-300 transform hover:scale-105 flex items-center"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
        </div>
      </nav>

      <div className="min-h-screen pt-20 p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg p-6 shadow-xl animate-slide-in" style={{ marginTop: '70px' }}>
            <h2 className="text-2xl font-semibold text-teal-600 mb-6">Meeting Report</h2>
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg border border-red-200 text-sm animate-slide-in">
                {error}
              </div>
            )}
            {/* Search and CSV Export */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
              <div className="relative w-full sm:w-64 mb-4 sm:mb-0">
                <input
                  type="text"
                  placeholder="Search by purpose..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all duration-300 hover:border-teal-400"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                  <SearchIcon />
                </div>
              </div>
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-green-500 text-white rounded-lg text-base font-medium hover:bg-green-600 flex items-center transition-all duration-300 transform hover:scale-105"
              >
                <DownloadIcon />
                Export CSV
              </button>
            </div>

            {/* Loading State */}
            {isLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-teal-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Purpose</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredMeetings.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500 text-sm">No meetings found</td>
                      </tr>
                    ) : (
                      filteredMeetings.map((meeting) => (
                        <React.Fragment key={meeting.id}>
                          <tr className="hover:bg-gray-50 transition-all duration-200">
                            <td className="px-6 py-4 text-sm text-gray-900">{meeting.purpose}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{meeting.location}</td>
                            <td className="px-6 py-4 text-sm text-gray-500">{new Date(meeting.time).toLocaleString()}</td>
                            <td className="px-6 py-4 text-sm text-gray-500 capitalize">{meeting.status}</td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => toggleDropdown(meeting.id)}
                                className="text-teal-600 hover:text-teal-800 transition-all duration-300 transform hover:scale-110"
                              >
                                {openMeetingId === meeting.id ? <MinusIcon /> : <PlusIcon />}
                              </button>
                            </td>
                          </tr>
                          {openMeetingId === meeting.id && (
                            <tr>
                              <td colSpan="5" className="px-6 py-4 bg-gray-50">
                                <div className="animate-slide-down">
                                  <h4 className="text-sm font-medium text-gray-900 mb-2">Attendees</h4>
                                  {meeting.attendees.length === 0 ? (
                                    <p className="text-sm text-gray-500">No attendees found</p>
                                  ) : (
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-teal-50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Email</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Phone</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">Role</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {meeting.attendees.map((attendee) => (
                                          <tr key={attendee.id} className="hover:bg-gray-50 transition-all duration-200">
                                            <td className="px-4 py-2 text-sm text-gray-900">{attendee.name}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{attendee.email}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500">{attendee.phone}</td>
                                            <td className="px-4 py-2 text-sm text-gray-500 capitalize">{attendee.role || "User"}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tailwind Animation Styles */}
      <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        .animate-slide-down {
          animation: slide-down 0.4s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MeetingReport;